import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  PenTool,
  Highlighter,
  Eraser,
  StickyNote as StickyIcon,
  Download,
  ExternalLink,
  CheckCircle2,
  Minus,
  Plus,
  MousePointer,
  Trash2,
  X,
  Loader2,
  Check,
  GraduationCap,
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { fetchLessonTheory } from '../lib/supabase';
import { useData } from '../context/DataContext';
import { translations } from '../lib/i18n';
import { TheoryMarkdown } from '../components/TheoryMarkdown';
import type { DrawingStroke, StickyNote as StickyNoteType } from '../lib/types';
import {
  getSavedLessonAnnotations,
  saveLessonAnnotations,
  clearLessonAnnotations,
  getSavedTheoryFontSize,
  saveTheoryFontSize,
} from '../lib/storage';

type ActiveTool = 'select' | 'pen' | 'highlighter' | 'eraser' | 'note';

export const TEXT_HIGHLIGHT_COLORS = [
  { id: 'yellow', nameAr: 'أصفر', nameFr: 'Jaune', nameEn: 'Yellow', bg: '#fef08a', border: '#eab308', dot: '#eab308' },
  { id: 'green', nameAr: 'أخضر', nameFr: 'Vert', nameEn: 'Green', bg: '#bbf7d0', border: '#22c55e', dot: '#22c55e' },
  { id: 'blue', nameAr: 'أزرق', nameFr: 'Bleu', nameEn: 'Blue', bg: '#bae6fd', border: '#0284c7', dot: '#0284c7' },
  { id: 'pink', nameAr: 'وردي', nameFr: 'Rose', nameEn: 'Pink', bg: '#fbcfe8', border: '#ec4899', dot: '#ec4899' },
  { id: 'orange', nameAr: 'برتقالي', nameFr: 'Orange', nameEn: 'Orange', bg: '#fed7aa', border: '#f97316', dot: '#f97316' },
  { id: 'purple', nameAr: 'بنفسجي', nameFr: 'Violet', nameEn: 'Purple', bg: '#e9d5ff', border: '#a855f7', dot: '#a855f7' },
];

const COLOR_PRESETS = [
  { name: 'Yellow', pen: '#eab308', highlight: 'rgba(250, 204, 21, 0.42)', note: '#fef9c3' },
  { name: 'Green', pen: '#16a34a', highlight: 'rgba(74, 222, 128, 0.42)', note: '#dcfce7' },
  { name: 'Blue', pen: '#2563eb', highlight: 'rgba(96, 165, 250, 0.42)', note: '#dbeafe' },
  { name: 'Pink', pen: '#db2777', highlight: 'rgba(244, 114, 182, 0.42)', note: '#fce7f3' },
  { name: 'Orange', pen: '#ea580c', highlight: 'rgba(251, 146, 60, 0.42)', note: '#ffedd5' },
  { name: 'Ink', pen: '#1e293b', highlight: 'rgba(100, 116, 139, 0.35)', note: '#f1f5f9' },
];

export const TheoryStudioPage: React.FC = () => {
  const { lessonId = '' } = useParams<{ lessonId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const wilaya = searchParams.get('w') || 'all';
  const { lessons, subjects, language, theme, getLessonCount } = useData();
  const t = translations[language];
  const isRtl = language === 'ar';

  // Lesson & subject metadata
  const currentLesson = lessons.find((l) => l.id === lessonId);
  const currentSubject = subjects.find((s) => s.id === currentLesson?.subject_id);
  const questionCount = currentLesson ? getLessonCount(currentLesson.id, wilaya) : 0;

  // Content loading state
  const [lessonName, setLessonName] = useState<string>(currentLesson?.name || '');
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Studio tool states
  const [activeTool, setActiveTool] = useState<ActiveTool>('select');
  const [selectedColorIdx, setSelectedColorIdx] = useState<number>(0);
  const [strokeWidth, setStrokeWidth] = useState<number>(3);
  const [fontSize, setFontSize] = useState<number>(getSavedTheoryFontSize);

  // Annotations: strokes & notes
  const [strokes, setStrokes] = useState<DrawingStroke[]>([]);
  const [notes, setNotes] = useState<StickyNoteType[]>([]);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [currentStroke, setCurrentStroke] = useState<{ x: number; y: number }[]>([]);

  // Text selection & word highlight state
  const [hasTextHighlights, setHasTextHighlights] = useState<boolean>(false);
  const [initialHtml, setInitialHtml] = useState<string | null>(null);
  const [markdownKey, setMarkdownKey] = useState<number>(0);
  const [floatingHighlight, setFloatingHighlight] = useState<{
    x: number;
    y: number;
    selectedText: string;
    existingMark: HTMLElement | null;
  } | null>(null);

  const savedRangeRef = useRef<Range | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const justSelectedRef = useRef<boolean>(false);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // 1. Fetch lesson theory
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchLessonTheory(lessonId)
      .then((data) => {
        if (!isMounted) return;
        setLessonName(data.name);
        setContent(data.theory);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err.message || 'Failed to load lesson theory');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [lessonId]);

  // 2. Load saved drawings and sticky notes
  useEffect(() => {
    if (lessonId) {
      const saved = getSavedLessonAnnotations(lessonId);
      setStrokes(saved.strokes || []);
      setNotes(saved.notes || []);
    }
  }, [lessonId]);

  // 3. Restore saved word highlights from localStorage after content loads
  useEffect(() => {
    if (lessonId) {
      const savedHtmlKey = `theory_highlights_html_${lessonId}`;
      const savedHtml = localStorage.getItem(savedHtmlKey);
      if (savedHtml) {
        setInitialHtml(savedHtml);
        setHasTextHighlights(true);
      } else {
        setInitialHtml(null);
        setHasTextHighlights(false);
      }
    }
  }, [lessonId]);

  // 4. Save annotations whenever they change
  const saveAnnotationsDebounced = useCallback(
    (newStrokes: DrawingStroke[], newNotes: StickyNoteType[]) => {
      if (!lessonId) return;
      saveLessonAnnotations(lessonId, { strokes: newStrokes, notes: newNotes });
    },
    [lessonId]
  );

  // 5. Canvas sizing & redraw
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = container.scrollWidth;
    const height = container.scrollHeight;

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    ctx.clearRect(0, 0, width, height);

    // Render strokes
    const allStrokes = [...strokes];
    if (currentStroke.length > 1) {
      const isHighlighter = activeTool === 'highlighter';
      const colorPreset = COLOR_PRESETS[selectedColorIdx];
      allStrokes.push({
        color: isHighlighter ? colorPreset.highlight : colorPreset.pen,
        width: isHighlighter ? strokeWidth * 5 : strokeWidth,
        points: currentStroke,
        isHighlighter,
      });
    }

    for (const stroke of allStrokes) {
      if (!stroke.points || stroke.points.length < 2) continue;

      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (stroke.isHighlighter) {
        ctx.globalCompositeOperation = 'multiply';
      } else {
        ctx.globalCompositeOperation = 'source-over';
      }

      ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
      for (let i = 1; i < stroke.points.length; i++) {
        ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
      }
      ctx.stroke();
      ctx.restore();
    }
  }, [strokes, currentStroke, activeTool, selectedColorIdx, strokeWidth]);

  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas, content, fontSize]);

  // Resize listener
  useEffect(() => {
    const handleResize = () => {
      redrawCanvas();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [redrawCanvas]);

  // Canvas Coordinates
  const getCanvasCoords = (e: React.MouseEvent | React.TouchEvent) => {
    const container = containerRef.current;
    if (!container) return { x: 0, y: 0 };

    const rect = container.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    if (activeTool === 'select') return;

    if (activeTool === 'note') {
      const coords = getCanvasCoords(e);
      const colorPreset = COLOR_PRESETS[selectedColorIdx];
      const newNote: StickyNoteType = {
        id: `note_${Date.now()}`,
        x: Math.max(10, coords.x - 20),
        y: Math.max(10, coords.y - 20),
        text: '',
        color: colorPreset.note,
      };
      const updatedNotes = [...notes, newNote];
      setNotes(updatedNotes);
      saveAnnotationsDebounced(strokes, updatedNotes);
      setActiveTool('select');
      return;
    }

    if (activeTool === 'eraser') {
      const coords = getCanvasCoords(e);
      eraseAtPoint(coords.x, coords.y);
      setIsDrawing(true);
      return;
    }

    if (activeTool === 'pen' || activeTool === 'highlighter') {
      setIsDrawing(true);
      const coords = getCanvasCoords(e);
      setCurrentStroke([coords]);
    }
  };

  const handlePointerMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;

    const coords = getCanvasCoords(e);

    if (activeTool === 'eraser') {
      eraseAtPoint(coords.x, coords.y);
      return;
    }

    if (activeTool === 'pen' || activeTool === 'highlighter') {
      setCurrentStroke((prev) => [...prev, coords]);
    }
  };

  const handlePointerUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if ((activeTool === 'pen' || activeTool === 'highlighter') && currentStroke.length > 1) {
      const isHighlighter = activeTool === 'highlighter';
      const colorPreset = COLOR_PRESETS[selectedColorIdx];
      const newStroke: DrawingStroke = {
        color: isHighlighter ? colorPreset.highlight : colorPreset.pen,
        width: isHighlighter ? strokeWidth * 5 : strokeWidth,
        points: currentStroke,
        isHighlighter,
      };
      const updatedStrokes = [...strokes, newStroke];
      setStrokes(updatedStrokes);
      saveAnnotationsDebounced(updatedStrokes, notes);
    }
    setCurrentStroke([]);
  };

  const eraseAtPoint = (x: number, y: number) => {
    const threshold = 18;
    const remaining = strokes.filter((stroke) => {
      return !stroke.points.some((p) => {
        const dx = p.x - x;
        const dy = p.y - y;
        return Math.sqrt(dx * dx + dy * dy) < threshold;
      });
    });

    if (remaining.length !== strokes.length) {
      setStrokes(remaining);
      saveAnnotationsDebounced(remaining, notes);
    }
  };

  // Sticky Notes handlers
  const handleUpdateNoteText = (id: string, text: string) => {
    const updated = notes.map((n) => (n.id === id ? { ...n, text } : n));
    setNotes(updated);
    saveAnnotationsDebounced(strokes, updated);
  };

  const handleDeleteNote = (id: string) => {
    const updated = notes.filter((n) => n.id !== id);
    setNotes(updated);
    saveAnnotationsDebounced(strokes, updated);
  };

  // Font controls
  const handleFontChange = (delta: number) => {
    const next = Math.max(13, Math.min(24, fontSize + delta));
    setFontSize(next);
    saveTheoryFontSize(next);
  };

  // =========================================================================
  // INTERACTIVE WORD & TEXT SELECTION HIGHLIGHTING
  // =========================================================================

  const persistHighlights = useCallback(() => {
    const markdownEl = document.getElementById('theory-markdown-content');
    if (markdownEl && lessonId) {
      const html = markdownEl.innerHTML;
      localStorage.setItem(`theory_highlights_html_${lessonId}`, html);
      const hasMarks = markdownEl.querySelectorAll('mark.theory-text-highlight').length > 0;
      setHasTextHighlights(hasMarks);
    }
  }, [lessonId]);

  // Safe DOM text-node highlighting across single words or multi-node blocks
  const highlightRangeInMarkdown = (range: Range, color: string): HTMLElement | null => {
    const markdownEl = document.getElementById('theory-markdown-content');
    if (!markdownEl || !markdownEl.contains(range.commonAncestorContainer)) return null;

    const createMark = () => {
      const mark = document.createElement('mark');
      mark.className = 'theory-text-highlight';
      mark.style.backgroundColor = color;
      mark.style.color = '#0f172a';
      mark.style.borderRadius = '3px';
      mark.style.padding = '1px 3px';
      mark.style.cursor = 'pointer';
      mark.style.boxDecorationBreak = 'clone';
      mark.style.setProperty('-webkit-box-decoration-break', 'clone');
      mark.setAttribute('data-color', color);
      return mark;
    };

    // Case 1: Start and end container are within the same text node (standard word/sentence selection)
    if (range.startContainer === range.endContainer && range.startContainer.nodeType === Node.TEXT_NODE) {
      const textNode = range.startContainer as Text;
      const start = range.startOffset;
      const end = range.endOffset;
      if (start === end) return null;

      textNode.splitText(end);
      const middle = textNode.splitText(start);

      const mark = createMark();
      textNode.parentNode?.insertBefore(mark, middle);
      mark.appendChild(middle);
      return mark;
    }

    // Case 2: Selection spans across elements (e.g. bold, italics, lists, or multiple paragraphs)
    const treeWalker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_TEXT,
      null
    );

    const textNodes: Text[] = [];
    let curr = treeWalker.nextNode();
    while (curr) {
      const tNode = curr as Text;
      if (range.intersectsNode(tNode) && tNode.nodeValue && tNode.nodeValue.length > 0) {
        textNodes.push(tNode);
      }
      curr = treeWalker.nextNode();
    }

    if (textNodes.length === 0) {
      try {
        const mark = createMark();
        const fragment = range.extractContents();
        mark.appendChild(fragment);
        range.insertNode(mark);
        return mark;
      } catch {
        return null;
      }
    }

    let firstMark: HTMLElement | null = null;
    for (let i = 0; i < textNodes.length; i++) {
      const tNode = textNodes[i];
      const isStartNode = tNode === range.startContainer;
      const isEndNode = tNode === range.endContainer;
      const len = tNode.nodeValue?.length || 0;

      const startOffset = isStartNode ? range.startOffset : 0;
      const endOffset = isEndNode ? range.endOffset : len;

      if (startOffset >= endOffset) continue;

      if (endOffset < len) {
        tNode.splitText(endOffset);
      }
      const middle = startOffset > 0 ? tNode.splitText(startOffset) : tNode;

      const mark = createMark();
      middle.parentNode?.insertBefore(mark, middle);
      mark.appendChild(middle);
      if (!firstMark) firstMark = mark;
    }

    return firstMark;
  };

  // Check text selection in the document
  const handleTextSelection = useCallback(() => {
    if (activeTool !== 'select') return;

    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      return;
    }

    const text = selection.toString().trim();
    if (!text) return;

    const range = selection.getRangeAt(0);
    const markdownEl = document.getElementById('theory-markdown-content');
    if (!markdownEl || !markdownEl.contains(range.commonAncestorContainer)) {
      return;
    }

    const rect = range.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;

    savedRangeRef.current = range.cloneRange();

    const popupWidth = 240;
    const bubbleX = Math.max(
      popupWidth / 2 + 16,
      Math.min(window.innerWidth - popupWidth / 2 - 16, rect.left + rect.width / 2)
    );
    const bubbleY = Math.max(50, rect.top - 12);

    setFloatingHighlight({
      x: bubbleX,
      y: bubbleY,
      selectedText: text,
      existingMark: null,
    });

    justSelectedRef.current = true;
    setTimeout(() => {
      justSelectedRef.current = false;
    }, 300);
  }, [activeTool]);

  // Continuous listener for selection changes (double-click, drag, mobile handles)
  useEffect(() => {
    if (activeTool !== 'select') {
      setFloatingHighlight(null);
      return;
    }

    let timer: number;
    const handleSelectionChange = () => {
      clearTimeout(timer);
      timer = window.setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed || !selection.toString().trim()) {
          setFloatingHighlight((prev) => (prev && !prev.existingMark ? null : prev));
          return;
        }
        handleTextSelection();
      }, 150);
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [activeTool, handleTextSelection]);

  // Click on existing highlight or empty space
  const handleArticleClick = (e: React.MouseEvent) => {
    if (justSelectedRef.current) {
      return;
    }

    const target = e.target as HTMLElement;

    // Never close if clicking inside popup bubble
    if (popupRef.current && popupRef.current.contains(target)) {
      return;
    }

    // Check if clicked an existing highlighted word
    const mark = target.closest('mark.theory-text-highlight') as HTMLElement;
    if (mark) {
      e.stopPropagation();
      const rect = mark.getBoundingClientRect();
      const popupWidth = 240;
      const bubbleX = Math.max(
        popupWidth / 2 + 16,
        Math.min(window.innerWidth - popupWidth / 2 - 16, rect.left + rect.width / 2)
      );
      const bubbleY = Math.max(50, rect.top - 12);

      savedRangeRef.current = null;
      setFloatingHighlight({
        x: bubbleX,
        y: bubbleY,
        selectedText: mark.textContent || '',
        existingMark: mark,
      });
      return;
    }

    // If text is still currently selected in the markdown content, keep popup open
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && selection.toString().trim().length > 0) {
      const markdownEl = document.getElementById('theory-markdown-content');
      if (markdownEl && selection.rangeCount > 0 && markdownEl.contains(selection.getRangeAt(0).commonAncestorContainer)) {
        return;
      }
    }

    // Dismiss popup
    setFloatingHighlight(null);
    savedRangeRef.current = null;
  };

  // Apply chosen color to selected text or existing mark
  const applyTextHighlight = (color: string) => {
    if (floatingHighlight?.existingMark) {
      floatingHighlight.existingMark.style.backgroundColor = color;
      floatingHighlight.existingMark.setAttribute('data-color', color);
      persistHighlights();
      setFloatingHighlight(null);
      return;
    }

    const range = savedRangeRef.current;
    if (!range) return;

    try {
      highlightRangeInMarkdown(range, color);
      window.getSelection()?.removeAllRanges();
      persistHighlights();
    } catch (err) {
      console.error('Highlight error:', err);
    } finally {
      setFloatingHighlight(null);
      savedRangeRef.current = null;
    }
  };

  // Remove highlight from word
  const removeTextHighlight = () => {
    if (floatingHighlight?.existingMark) {
      const mark = floatingHighlight.existingMark;
      const parent = mark.parentNode;
      if (parent) {
        while (mark.firstChild) {
          parent.insertBefore(mark.firstChild, mark);
        }
        mark.remove();
        parent.normalize();
        persistHighlights();
      }
    }
    setFloatingHighlight(null);
    savedRangeRef.current = null;
  };

  // Clear all annotations (drawings, notes, AND text highlights)
  const handleClearDrawings = () => {
    const confirmClear = window.confirm(
      language === 'ar'
        ? 'هل تريد مسح جميع الرسومات والملاحظات وتلوين الكلمات في هذا الدرس؟'
        : 'Supprimer tous les dessins, notes et surlignages de ce cours ?'
    );
    if (confirmClear) {
      setStrokes([]);
      setNotes([]);
      clearLessonAnnotations(lessonId);
      localStorage.removeItem(`theory_highlights_html_${lessonId}`);
      setHasTextHighlights(false);
      setInitialHtml(null);
      setMarkdownKey((k) => k + 1);
    }
  };

  // =========================================================================
  // PUBLICATION-GRADE, PURE WHITE MULTI-PAGE PDF GENERATION
  // =========================================================================
  const [pdfActionLoading, setPdfActionLoading] = useState<'download' | 'native' | null>(null);
  const isExportingPdf = pdfActionLoading !== null;
  const [exportSuccess, setExportSuccess] = useState<boolean>(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const buildCleanOrganizedPdf = async (): Promise<jsPDF | null> => {
    const markdownEl = document.getElementById('theory-markdown-content');
    if (!markdownEl) return null;

    // 1. Create a dedicated off-screen staging area
    // Strictly pure white background, crisp charcoal typography, no dark theme styles
    const stagingWrapper = document.createElement('div');
    stagingWrapper.className = 'pdf-export-staging-sandbox';
    stagingWrapper.style.position = 'fixed';
    stagingWrapper.style.left = '-9999px';
    stagingWrapper.style.top = '0';
    stagingWrapper.style.width = '794px'; // 210mm at 96 DPI
    stagingWrapper.style.backgroundColor = '#ffffff';
    stagingWrapper.style.color = '#111827';
    stagingWrapper.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
    stagingWrapper.style.zIndex = '-999';

    document.body.appendChild(stagingWrapper);

    try {
      const sourceBlocks = Array.from(markdownEl.children);

      const A4_PAGE_HEIGHT_PX = 1123; // 297mm at 96 DPI
      const PAGE_PADDING_TOP_BOTTOM = 40;
      const HEADER_HEIGHT_PX = 85;
      const FOOTER_HEIGHT_PX = 45;
      const MAX_CONTENT_HEIGHT_PX = A4_PAGE_HEIGHT_PX - (PAGE_PADDING_TOP_BOTTOM * 2 + HEADER_HEIGHT_PX + FOOTER_HEIGHT_PX); // ~913px

      const pageElements: HTMLElement[] = [];

      const createNewPage = (pageNum: number) => {
        const page = document.createElement('div');
        page.className = 'pdf-a4-clean-page';
        page.style.width = '794px';
        page.style.height = `${A4_PAGE_HEIGHT_PX}px`;
        page.style.maxHeight = `${A4_PAGE_HEIGHT_PX}px`;
        page.style.padding = '36px 44px';
        page.style.boxSizing = 'border-box';
        page.style.backgroundColor = '#ffffff';
        page.style.color = '#111827';
        page.style.display = 'flex';
        page.style.flexDirection = 'column';
        page.style.justifyContent = 'space-between';
        page.style.overflow = 'hidden';
        page.style.position = 'relative';

        // Page Header
        const header = document.createElement('div');
        header.className = 'pdf-a4-header';
        header.style.display = 'flex';
        header.style.justifyContent = 'space-between';
        header.style.alignItems = 'center';
        header.style.paddingBottom = '12px';
        header.style.marginBottom = '18px';
        header.style.borderBottom = '2px solid #0284c7';
        header.innerHTML = `
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="width: 26px; height: 26px; border-radius: 6px; background: #0284c7; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: bold;">
              M
            </div>
            <div>
              <div style="font-size: 13px; font-weight: 800; color: #0f172a; letter-spacing: -0.01em;">
                ${language === 'ar' ? 'الدروس والامتحانات الطبية · MedIntel' : 'MedIntel · Plateforme Médicale'}
              </div>
              <div style="font-size: 10px; color: #64748b;">
                ${language === 'ar' ? 'دليل دراسة وسند سريري رسمي' : 'Document d’étude officiel & Synthèse clinique'}
              </div>
            </div>
          </div>
          <div style="text-align: right; display: flex; flex-direction: column; align-items: flex-end;">
            <span style="font-size: 11px; font-weight: 700; color: #0284c7; background: #f0f9ff; padding: 2px 10px; border-radius: 999px; border: 1px solid #bae6fd;">
              ${currentSubject?.name || 'Module'}
            </span>
            <span style="font-size: 9px; color: #94a3b8; margin-top: 3px;">
              ${new Date().toLocaleDateString(language === 'ar' ? 'ar-DZ' : 'fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
        `;

        // Page Body Container
        const body = document.createElement('div');
        body.className = 'pdf-a4-body';
        body.style.flex = '1';
        body.style.overflow = 'hidden';
        body.style.display = 'flex';
        body.style.flexDirection = 'column';
        body.style.gap = '10px';
        body.style.fontSize = '13.5px';
        body.style.lineHeight = '1.65';
        body.style.color = '#1f2937';

        // Page Footer
        const footer = document.createElement('div');
        footer.className = 'pdf-a4-footer';
        footer.style.display = 'flex';
        footer.style.justifyContent = 'space-between';
        footer.style.alignItems = 'center';
        footer.style.paddingTop = '10px';
        footer.style.marginTop = '12px';
        footer.style.borderTop = '1px solid #e2e8f0';
        footer.style.fontSize = '10px';
        footer.style.color = '#64748b';
        footer.innerHTML = `
          <div style="font-weight: 600; color: #334155; max-width: 320px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
            ${lessonName || 'Cours Médical'}
          </div>
          <div>MedIntel - Révision & Concours</div>
          <div class="pdf-page-number-slot" style="font-weight: 700; color: #0f172a;">
            Page ${pageNum}
          </div>
        `;

        page.appendChild(header);
        page.appendChild(body);
        page.appendChild(footer);
        stagingWrapper.appendChild(page);

        return { page, body };
      };

      let currentPageIdx = 1;
      let current = createNewPage(currentPageIdx);
      pageElements.push(current.page);

      // Distribute markdown content into pages safely
      for (const sourceBlock of sourceBlocks) {
        const cloned = sourceBlock.cloneNode(true) as HTMLElement;

        // Force clean white-paper styling
        cloned.style.color = '#1f2937';
        cloned.style.backgroundColor = 'transparent';
        cloned.style.margin = '0 0 10px 0';

        // Headings
        if (['H1', 'H2', 'H3', 'H4'].includes(cloned.tagName)) {
          cloned.style.color = '#0f172a';
          cloned.style.fontWeight = '800';
          cloned.style.marginTop = '8px';
          cloned.style.marginBottom = '6px';
          if (cloned.tagName === 'H1') cloned.style.fontSize = '20px';
          if (cloned.tagName === 'H2') cloned.style.fontSize = '17px';
          if (cloned.tagName === 'H3') cloned.style.fontSize = '15px';
        }

        // Tables
        const tableWrapper = cloned.classList.contains('theory-table-wrapper')
          ? cloned
          : (cloned.querySelector('.theory-table-wrapper') as HTMLElement);
        if (tableWrapper) {
          tableWrapper.style.border = '1px solid #cbd5e1';
          tableWrapper.style.borderRadius = '6px';
          tableWrapper.style.overflow = 'hidden';
          tableWrapper.style.margin = '8px 0';
          const tbl = tableWrapper.querySelector('table');
          if (tbl) {
            tbl.style.width = '100%';
            tbl.style.borderCollapse = 'collapse';
            tbl.style.fontSize = '12px';
            tbl.querySelectorAll('th').forEach((th) => {
              th.style.background = '#f1f5f9';
              th.style.color = '#0f172a';
              th.style.padding = '6px 10px';
              th.style.borderBottom = '2px solid #cbd5e1';
              th.style.textAlign = 'start';
            });
            tbl.querySelectorAll('td').forEach((td) => {
              td.style.padding = '6px 10px';
              td.style.borderBottom = '1px solid #e2e8f0';
              td.style.color = '#334155';
            });
          }
        }

        // Callouts
        const callout = cloned.classList.contains('callout-box')
          ? cloned
          : (cloned.querySelector('.callout-box') as HTMLElement);
        if (callout) {
          callout.style.backgroundColor = '#f8fafc';
          callout.style.border = '1.5px solid #cbd5e1';
          callout.style.borderRadius = '8px';
          callout.style.padding = '10px 14px';
          callout.style.margin = '8px 0';
          const calloutHeader = callout.querySelector('.callout-header') as HTMLElement;
          if (calloutHeader) {
            calloutHeader.style.color = '#0f172a';
            calloutHeader.style.fontWeight = '700';
            calloutHeader.style.marginBottom = '4px';
          }
        }

        // Preserve highlighted marks cleanly
        cloned.querySelectorAll('mark.theory-text-highlight').forEach((m) => {
          const mark = m as HTMLElement;
          const color = mark.getAttribute('data-color') || mark.style.backgroundColor || '#fef08a';
          mark.style.backgroundColor = color;
          mark.style.color = '#0f172a';
          mark.style.padding = '1px 3px';
          mark.style.borderRadius = '3px';
          mark.style.fontWeight = '600';
        });

        // Test appending to current body
        current.body.appendChild(cloned);

        if (current.body.scrollHeight > MAX_CONTENT_HEIGHT_PX) {
          current.body.removeChild(cloned);

          // If list with multiple items, split items across pages cleanly
          if (cloned.tagName === 'UL' || cloned.tagName === 'OL') {
            const items = Array.from(cloned.children) as HTMLElement[];
            let currentList = document.createElement(cloned.tagName.toLowerCase()) as HTMLElement;
            currentList.style.paddingInlineStart = '20px';
            currentList.style.margin = '4px 0';
            current.body.appendChild(currentList);

            for (const item of items) {
              const itemClone = item.cloneNode(true) as HTMLElement;
              currentList.appendChild(itemClone);
              if (current.body.scrollHeight > MAX_CONTENT_HEIGHT_PX) {
                currentList.removeChild(itemClone);
                currentPageIdx++;
                current = createNewPage(currentPageIdx);
                pageElements.push(current.page);

                currentList = document.createElement(cloned.tagName.toLowerCase()) as HTMLElement;
                currentList.style.paddingInlineStart = '20px';
                currentList.style.margin = '4px 0';
                currentList.appendChild(itemClone);
                current.body.appendChild(currentList);
              }
            }
          } else {
            // Normal block: start new page and put block there
            currentPageIdx++;
            current = createNewPage(currentPageIdx);
            pageElements.push(current.page);
            current.body.appendChild(cloned);
          }
        }
      }

      // Include sticky notes if any exist
      if (notes.length > 0) {
        const notesBlock = document.createElement('div');
        notesBlock.style.marginTop = '12px';
        notesBlock.style.padding = '12px 14px';
        notesBlock.style.backgroundColor = '#fef9c3';
        notesBlock.style.border = '1.5px solid #fde047';
        notesBlock.style.borderRadius = '8px';
        notesBlock.innerHTML = `
          <div style="font-weight: 700; color: #854d0e; font-size: 13px; margin-bottom: 6px;">
            📌 ${language === 'ar' ? 'ملاحظات وتلخيصات الطالب(ة) في هذا الدرس' : 'Notes cliniques & mémos personnels'}
          </div>
          ${notes
            .map(
              (n) => `
            <div style="font-size: 12px; color: #713f12; margin-bottom: 4px; padding-inline-start: 8px; border-inline-start: 2px solid #eab308;">
              ${n.text || (language === 'ar' ? '(ملاحظة فارغة)' : '(note vide)')}
            </div>
          `
            )
            .join('')}
        `;

        if (current.body.scrollHeight + 150 > MAX_CONTENT_HEIGHT_PX) {
          currentPageIdx++;
          current = createNewPage(currentPageIdx);
          pageElements.push(current.page);
        }
        current.body.appendChild(notesBlock);
      }

      // Update total pages in all footers
      const totalPages = pageElements.length;
      pageElements.forEach((pageEl, idx) => {
        const slot = pageEl.querySelector('.pdf-page-number-slot');
        if (slot) {
          slot.textContent =
            language === 'ar' ? `صفحة ${idx + 1} من ${totalPages}` : `Page ${idx + 1} / ${totalPages}`;
        }
      });

      // 3. Render discrete A4 pages into jsPDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      for (let i = 0; i < pageElements.length; i++) {
        if (i > 0) pdf.addPage();

        const pageEl = pageElements[i];
        const pageCanvas = await html2canvas(pageEl, {
          scale: 2, // 2x crispness
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: 794,
          height: A4_PAGE_HEIGHT_PX,
        });

        const imgData = pageCanvas.toDataURL('image/jpeg', 0.95);
        pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
      }

      return pdf;
    } finally {
      if (stagingWrapper.parentNode) {
        stagingWrapper.parentNode.removeChild(stagingWrapper);
      }
    }
  };

  // Direct PDF file download
  const handleExportPdf = async () => {
    if (isExportingPdf) return;
    setPdfActionLoading('download');
    setExportSuccess(false);
    setExportError(null);

    try {
      const pdf = await buildCleanOrganizedPdf();
      if (!pdf) return;

      const safeTitle = (lessonName || 'MedQCM_Cours')
        .replace(/[/\\?%*:|"<>]/g, '_')
        .trim()
        .slice(0, 50);
      pdf.save(`${safeTitle}.pdf`);

      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 4500);
    } catch (err) {
      console.error('Failed to export PDF:', err);
      setExportError(t.pdfExportError);
      setTimeout(() => setExportError(null), 4000);
    } finally {
      setPdfActionLoading(null);
    }
  };

  // Open directly in the system / browser's official PDF reader
  const handleOpenInOfficialPdfReader = async () => {
    if (isExportingPdf) return;
    setPdfActionLoading('native');
    setExportSuccess(false);
    setExportError(null);

    try {
      const pdf = await buildCleanOrganizedPdf();
      if (!pdf) return;

      const pdfBlob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(pdfBlob);

      const newWin = window.open(blobUrl, '_blank');
      if (!newWin) {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }

      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 4500);
    } catch (err) {
      console.error('Failed to open PDF in official viewer:', err);
      setExportError(t.pdfExportError);
      setTimeout(() => setExportError(null), 4000);
    } finally {
      setPdfActionLoading(null);
    }
  };

  const handleGoToQuiz = () => {
    navigate(`/quiz/${lessonId}?w=${encodeURIComponent(wilaya)}`);
  };

  return (
    <div className="theory-studio-page" data-theme={theme} onClick={handleArticleClick}>
      {/* ============================================================
          TOP BAR (Navigation & Actions - Clean, Spacious, Uncramped)
          ============================================================ */}
      <header className="theory-studio-topbar no-print">
        {/* Left: Back & Breadcrumb */}
        <div className="studio-topbar-start">
          <button
            type="button"
            className="icon-action-btn"
            onClick={() => navigate(-1)}
            title={t.previous}
            aria-label={t.previous}
          >
            {isRtl ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
          </button>

          <div className="studio-breadcrumb">
            <span className="studio-subject-pill">
              <GraduationCap size={13} />
              <span>{currentSubject?.name || t.theoryTitle}</span>
            </span>
            <span className="studio-breadcrumb-separator">/</span>
            <h1 className="studio-lesson-title" dir="auto" title={lessonName}>
              {lessonName || t.theoryTitle}
            </h1>
          </div>
        </div>

        {/* Right: Actions & CTAs */}
        <div className="studio-topbar-end">
          {/* Font Size Adjusters */}
          <div className="studio-font-controls" title={t.readingMode}>
            <button
              type="button"
              className="font-size-btn"
              onClick={() => handleFontChange(-1)}
              disabled={fontSize <= 13}
              title={t.fontSmaller}
            >
              <Minus size={13} />
            </button>
            <span className="font-size-label">{fontSize}px</span>
            <button
              type="button"
              className="font-size-btn"
              onClick={() => handleFontChange(1)}
              disabled={fontSize >= 24}
              title={t.fontLarger}
            >
              <Plus size={13} />
            </button>
          </div>

          {/* Open in Official PDF Reader */}
          <button
            type="button"
            className={`btn-secondary studio-action-btn ${pdfActionLoading === 'native' ? 'loading' : ''}`}
            onClick={handleOpenInOfficialPdfReader}
            disabled={isExportingPdf}
            title={t.officialPdfReaderTooltip}
          >
            {pdfActionLoading === 'native' ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                <span>{t.exportingPdf}</span>
              </>
            ) : (
              <>
                <ExternalLink size={15} />
                <span>{t.openInOfficialPdfReader}</span>
              </>
            )}
          </button>

          {/* Download PDF File */}
          <button
            type="button"
            className={`btn-secondary studio-action-btn ${pdfActionLoading === 'download' ? 'loading' : ''}`}
            onClick={handleExportPdf}
            disabled={isExportingPdf}
            title={t.savePdf}
          >
            {pdfActionLoading === 'download' ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                <span>{t.exportingPdf}</span>
              </>
            ) : exportSuccess ? (
              <>
                <Check size={15} style={{ color: '#059669' }} />
                <span>{t.pdfExportSuccess}</span>
              </>
            ) : (
              <>
                <Download size={15} />
                <span>{t.savePdf}</span>
              </>
            )}
          </button>

          {/* Jump to Exam Practice Button */}
          <button
            type="button"
            className="btn-primary studio-action-btn quiz-primary-cta"
            onClick={handleGoToQuiz}
          >
            <CheckCircle2 size={16} />
            <span>
              {t.startPracticeFromLesson} ({questionCount})
            </span>
          </button>
        </div>
      </header>

      {/* ============================================================
          DEDICATED STUDY & ANNOTATION DOCK (Spacious & Floating)
          ============================================================ */}
      <div className="theory-study-dock-wrapper no-print">
        <aside className="theory-study-dock" role="toolbar" aria-label={t.studyTools}>
          {/* Tool 1: Reading & Text Selection */}
          <button
            type="button"
            className={`dock-tool-btn ${activeTool === 'select' ? 'active' : ''}`}
            onClick={() => setActiveTool('select')}
            title={t.highlightInstruction}
          >
            <MousePointer size={15} />
            <span>{t.textHighlightMode}</span>
          </button>

          {/* Tool 2: Freehand Pen */}
          <button
            type="button"
            className={`dock-tool-btn ${activeTool === 'pen' ? 'active' : ''}`}
            onClick={() => setActiveTool('pen')}
            title={t.drawPen}
          >
            <PenTool size={15} />
            <span>{t.drawPen}</span>
          </button>

          {/* Tool 3: Freehand Highlighter */}
          <button
            type="button"
            className={`dock-tool-btn ${activeTool === 'highlighter' ? 'active' : ''}`}
            onClick={() => setActiveTool('highlighter')}
            title={t.highlighter}
          >
            <Highlighter size={15} />
            <span>{t.highlighter}</span>
          </button>

          {/* Tool 4: Sticky Note */}
          <button
            type="button"
            className={`dock-tool-btn ${activeTool === 'note' ? 'active' : ''}`}
            onClick={() => setActiveTool('note')}
            title={t.addNote}
          >
            <StickyIcon size={15} />
            <span>{t.addNote}</span>
          </button>

          {/* Tool 5: Eraser */}
          <button
            type="button"
            className={`dock-tool-btn ${activeTool === 'eraser' ? 'active' : ''}`}
            onClick={() => setActiveTool('eraser')}
            title={t.eraser}
          >
            <Eraser size={15} />
            <span>{t.eraser}</span>
          </button>

          <div className="dock-divider" />

          {/* Color Presets */}
          <div className="dock-palette">
            {TEXT_HIGHLIGHT_COLORS.map((c, idx) => (
              <button
                key={c.id}
                type="button"
                className={`dock-color-dot ${selectedColorIdx === idx ? 'active' : ''}`}
                style={{ backgroundColor: c.dot }}
                onClick={() => setSelectedColorIdx(idx)}
                title={language === 'ar' ? c.nameAr : c.nameFr}
                aria-label={c.id}
              />
            ))}
          </div>

          <div className="dock-divider" />

          {/* Stroke Size for Drawing */}
          <div className="dock-sizes">
            <button
              type="button"
              className={`dock-size-btn ${strokeWidth === 2 ? 'active' : ''}`}
              onClick={() => setStrokeWidth(2)}
              title="Fin"
            >
              <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'currentColor' }} />
            </button>
            <button
              type="button"
              className={`dock-size-btn ${strokeWidth === 4 ? 'active' : ''}`}
              onClick={() => setStrokeWidth(4)}
              title="Moyen"
            >
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'currentColor' }} />
            </button>
            <button
              type="button"
              className={`dock-size-btn ${strokeWidth === 7 ? 'active' : ''}`}
              onClick={() => setStrokeWidth(7)}
              title="Épais"
            >
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'currentColor' }} />
            </button>
          </div>

          {/* Clear Annotations (if any strokes, notes, or highlights exist) */}
          {(strokes.length > 0 || notes.length > 0 || hasTextHighlights) && (
            <>
              <div className="dock-divider" />
              <button
                type="button"
                className="dock-clear-btn"
                onClick={handleClearDrawings}
                title={t.clearDrawing}
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
        </aside>
      </div>

      {/* ============================================================
          FLOATING MINI COLOR PICKER FOR WORD SELECTION
          ============================================================ */}
      {floatingHighlight && (
        <div
          ref={popupRef}
          className="theory-floating-highlighter"
          style={{
            position: 'fixed',
            left: `${floatingHighlight.x}px`,
            top: `${floatingHighlight.y}px`,
            transform: 'translate(-50%, -100%)',
            zIndex: 9999,
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="floating-highlighter-bubble" onMouseDown={(e) => e.preventDefault()}>
            <span className="floating-highlighter-hint">
              {floatingHighlight.existingMark ? t.drawingColor : t.textHighlight}:
            </span>
            <div className="floating-highlighter-palette">
              {TEXT_HIGHLIGHT_COLORS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="floating-color-btn"
                  style={{ backgroundColor: c.dot }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    applyTextHighlight(c.bg);
                  }}
                  title={language === 'ar' ? c.nameAr : c.nameFr}
                />
              ))}
            </div>

            {floatingHighlight.existingMark && (
              <button
                type="button"
                className="floating-remove-btn"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeTextHighlight();
                }}
                title={t.removeHighlight}
              >
                <Trash2 size={13} />
              </button>
            )}

            <button
              type="button"
              className="floating-close-btn"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                setFloatingHighlight(null);
                savedRangeRef.current = null;
              }}
              title={isRtl ? 'إغلاق' : 'Fermer'}
            >
              <X size={12} />
            </button>
          </div>
          <div className="floating-highlighter-arrow" />
        </div>
      )}

      {/* ============================================================
          MAIN STUDY CONTENT + DRAWING OVERLAY
          ============================================================ */}
      <main className="theory-studio-main">
        {loading && (
          <div className="state-box" style={{ marginTop: '4rem' }}>
            <p className="state-title">{t.loadingData}</p>
          </div>
        )}

        {error && (
          <div className="state-box" style={{ marginTop: '4rem' }}>
            <h2 className="state-title">{t.loadingErrorTitle}</h2>
            <p className="state-desc">{error}</p>
          </div>
        )}

        {!loading && !error && (!content || !content.trim()) && (
          <div className="state-box" style={{ marginTop: '4rem' }}>
            <h2 className="state-title">{t.noLessonsFound}</h2>
            <button type="button" className="btn-primary" onClick={handleGoToQuiz}>
              <CheckCircle2 size={16} />
              <span>{t.startPracticeFromLesson}</span>
            </button>
          </div>
        )}

        {!loading && !error && content && content.trim() && (
          <div
            ref={containerRef}
            className={`theory-article-container ${activeTool !== 'select' ? 'drawing-active' : ''}`}
            style={{ fontSize: `${fontSize}px` }}
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={() => {
              handlePointerUp();
              if (activeTool === 'select') {
                handleTextSelection();
              }
            }}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={() => {
              handlePointerUp();
              if (activeTool === 'select') {
                setTimeout(handleTextSelection, 60);
              }
            }}
          >
            {/* Markdown Lesson Content */}
            <TheoryMarkdown
              key={`${lessonId}_${markdownKey}`}
              content={content}
              initialHtml={initialHtml}
            />

            {/* Transparent Drawing Canvas Overlay */}
            <canvas
              ref={canvasRef}
              className="theory-drawing-canvas"
              style={{
                pointerEvents:
                  activeTool === 'pen' || activeTool === 'highlighter' || activeTool === 'eraser'
                    ? 'auto'
                    : 'none',
              }}
            />

            {/* Sticky Notes Layer */}
            {notes.map((note) => (
              <div
                key={note.id}
                className="theory-sticky-note"
                style={{
                  top: `${note.y}px`,
                  left: `${note.x}px`,
                  backgroundColor: note.color,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky-note-header">
                  <StickyIcon size={12} />
                  <span>{t.addNote}</span>
                  <button
                    type="button"
                    className="sticky-note-del-btn"
                    onClick={() => handleDeleteNote(note.id)}
                    title={t.deleteNote}
                  >
                    <X size={12} />
                  </button>
                </div>
                <textarea
                  className="sticky-note-textarea"
                  value={note.text}
                  placeholder={t.typeNotePlaceholder}
                  onChange={(e) => handleUpdateNoteText(note.id, e.target.value)}
                  dir="auto"
                />
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Floating Bottom Quick Bar for Mobile */}
      <nav className="mobile-tools-bar no-print" aria-label="Mobile quick tools">
        <button
          type="button"
          className={`mobile-tool-icon-btn ${activeTool === 'select' ? 'active' : ''}`}
          onClick={() => setActiveTool('select')}
          title={t.readingMode}
        >
          <MousePointer size={18} />
        </button>
        <button
          type="button"
          className={`mobile-tool-icon-btn ${activeTool === 'pen' ? 'active' : ''}`}
          onClick={() => setActiveTool('pen')}
          title={t.drawPen}
        >
          <PenTool size={18} />
        </button>
        <button
          type="button"
          className={`mobile-tool-icon-btn ${activeTool === 'highlighter' ? 'active' : ''}`}
          onClick={() => setActiveTool('highlighter')}
          title={t.highlighter}
        >
          <Highlighter size={18} />
        </button>
        <button
          type="button"
          className={`mobile-tool-icon-btn ${activeTool === 'note' ? 'active' : ''}`}
          onClick={() => setActiveTool('note')}
          title={t.addNote}
        >
          <StickyIcon size={18} />
        </button>
        <button
          type="button"
          className={`mobile-tool-icon-btn ${activeTool === 'eraser' ? 'active' : ''}`}
          onClick={() => setActiveTool('eraser')}
          title={t.eraser}
        >
          <Eraser size={18} />
        </button>

        {/* Mobile Open in Official PDF Reader */}
        <button
          type="button"
          className={`mobile-tool-icon-btn ${pdfActionLoading === 'native' ? 'loading' : ''}`}
          onClick={handleOpenInOfficialPdfReader}
          disabled={isExportingPdf}
          title={t.openInOfficialPdfReader}
        >
          {pdfActionLoading === 'native' ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <ExternalLink size={18} />
          )}
        </button>

        {/* Mobile Download PDF */}
        <button
          type="button"
          className={`mobile-tool-icon-btn ${pdfActionLoading === 'download' ? 'loading' : ''}`}
          onClick={handleExportPdf}
          disabled={isExportingPdf}
          title={t.savePdf}
        >
          {pdfActionLoading === 'download' ? (
            <Loader2 size={18} className="animate-spin" />
          ) : exportSuccess ? (
            <Check size={18} style={{ color: '#059669' }} />
          ) : (
            <Download size={18} />
          )}
        </button>

        <button
          type="button"
          className="mobile-mcq-jump-btn"
          onClick={handleGoToQuiz}
        >
          <CheckCircle2 size={16} />
          <span>QCM ({questionCount})</span>
        </button>
      </nav>

      {/* Floating Toast Notification for PDF Generation Status */}
      {exportSuccess && (
        <div className="pdf-toast-notification success" role="alert">
          <CheckCircle2 size={18} />
          <span>{t.pdfExportSuccess}</span>
        </div>
      )}

      {exportError && (
        <div className="pdf-toast-notification error" role="alert">
          <span>{exportError}</span>
        </div>
      )}
    </div>
  );
};
